import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizRounded } from '@mui/icons-material';
import { FormControlLabel, Switch } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import type { TooltipProps } from '@mui/material/Tooltip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import color from 'src/assets/_util.scss';
import { useContextMenu } from 'src/components/core/ContextMenu';
import { HSpacer } from 'src/components/core/Spacer';
import { HStack, VStack } from 'src/components/core/Stack';
import { View } from 'src/components/core/View';
import {
  AIProvider,
  useAIContext,
} from 'src/components/Insights/Catalog/AIProvider';
import { CatalogContextMenu } from 'src/components/Insights/Catalog/CatalogContextMenu';
import {
  CatalogPageProvider,
  Tab,
  useCatalogPageContext,
} from 'src/components/Insights/Catalog/CatalogPageProvider';
import { CodebookProvider } from 'src/components/Insights/Catalog/CodebookProvider';
import { HighlightsPageProvider } from 'src/components/Insights/Catalog/HighlightsPageProvider';
import TabNavigation from 'src/components/Insights/Catalog/NavigationTabs';
import { MobileCatalogChip } from 'src/components/Insights/Catalogs/CatalogCard';
import ModalDialog from 'src/components/Insights/Dialogs/ModalDialog';
import Layout from 'src/components/Layout/Layout';
import NotFoundPage from '../Errors/NotFoundPage';
import { CodebookModern } from './CodebookModern';
import ConversationsPage from './ConversationsPage';
import { ConversationsPageProvider } from './ConversationsPageProvider';
import HighlightsPage from './HighlightsPage';
import OverviewPage from './OverviewPage';
import SensemakersPage from './SensemakersPage';

import './Catalog.module.scss';

export default function CatalogPage() {
  const { t } = useTranslation();
  return (
    <CatalogPageProvider>
      <AIProvider>
        <CodebookProvider>
          <HighlightsPageProvider>
            <ConversationsPageProvider>
              <Layout
                className="CatalogRoute"
                title={t('main_nav.insights')}
                style={{ padding: 0 }}
                contentStyle={{ padding: 0, overflow: 'hidden' }}
              >
                <CatalogPageContent />
              </Layout>
            </ConversationsPageProvider>
          </HighlightsPageProvider>
        </CodebookProvider>
      </AIProvider>
    </CatalogPageProvider>
  );
}

function CatalogPageContent() {
  const { activeTab, setActiveTab, modals, hasCodebook, codebookRef } =
    useCatalogPageContext();

  return (
    <HStack
      sx={{
        position: 'relative',
        flexBasis: '100vh',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <VStack sx={{ flexGrow: 1, background: color.white }}>
        <CatalogTitle />
        <TabNavigation {...{ activeTab, setActiveTab }} />

        <View sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          {{
            [Tab.Overview]: <OverviewPage />,
            [Tab.Conversations]: <ConversationsPage />,
            [Tab.Highlights]: <HighlightsPage />,
            [Tab.Sensemakers]: <SensemakersPage />,
          }[activeTab] ?? <NotFoundPage />}
        </View>
      </VStack>

      <div
        ref={codebookRef}
        style={{
          borderLeft: `1px solid ${color.gray400}`,
          flex: '0 0 auto',
          height: '100%',
          maxWidth: '385px',
        }}
      >
        {hasCodebook && <CodebookModern />}
      </div>

      {modals?.map((modalProps) => (
        <ModalDialog {...modalProps} />
      ))}
    </HStack>
  );
}

// SUBCOMPONENTS

function CatalogTitle() {
  const { catalog, showingHighlights, isLoading, codebookHasCodes, t } =
    useCatalogPageContext();
  const { aiOn, setAIOn } = useAIContext();
  const contextMenu = useContextMenu();

  return (
    <HStack sx={{ p: '20px 24px 12px', alignItems: 'center' }}>
      <Typography
        sx={{
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          minHeight: '38px',
          gap: 1,
        }}
        variant="h5"
      >
        {isLoading ? t('common.loading') : catalog?.title ?? '[untitled]'}
        {catalog.mobile && <MobileCatalogChip />}

        <IconButton
          onClick={() => contextMenu.setOpen(true)}
          sx={{
            p: 0,
            padding: '1px 6px',
            borderRadius: '12px',
            display: isLoading ? 'none' : 'inherit',
            background:
              color[contextMenu.open ? 'foraPurpleDark' : 'foraPurple'],
            ':hover': {
              background: color.foraPurpleDark,
            },
          }}
          ref={contextMenu.anchorRef as RefObject<HTMLButtonElement>}
          data-testid="catalog-more-button"
        >
          <MoreHorizRounded htmlColor={color.white} fontSize="small" />
        </IconButton>
      </Typography>

      <HSpacer grow />

      {showingHighlights && (
        <SuggestCodesTooltip
          placement="left"
          codebookHasCodes={codebookHasCodes}
        >
          <FormControlLabel
            checked={aiOn && codebookHasCodes}
            control={<Switch color="primary" />}
            onChange={() => setAIOn((prev) => !prev)}
            label={t('insights.aiSuggestionsToggle')}
            labelPlacement="start"
            disabled={!codebookHasCodes}
          />
        </SuggestCodesTooltip>
      )}

      {!isLoading && <CatalogContextMenu {...contextMenu} />}
    </HStack>
  );
}

export function SuggestCodesTooltip(props: {
  placement: TooltipProps['placement'];
  children: React.ReactElement;
  codebookHasCodes?: boolean;
  tooManyHighlights?: boolean;
}) {
  const { t } = useTranslation();
  const { placement, children, codebookHasCodes, tooManyHighlights } = props;
  const title = !codebookHasCodes ? (
    <span style={{ fontSize: '14px' }}>{t('insights.disabledAITooltip')}</span>
  ) : tooManyHighlights ? (
    <span style={{ fontSize: '14px' }}>
      {t('insights.ai_suggestion_limit')}
    </span>
  ) : undefined;

  return (
    <Tooltip
      enterTouchDelay={0.1}
      leaveTouchDelay={5000}
      arrow
      placement={placement}
      title={title}
      disableHoverListener={codebookHasCodes && !tooManyHighlights}
      disableFocusListener={codebookHasCodes && !tooManyHighlights}
    >
      {children}
    </Tooltip>
  );
}
